import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
  makeStyles,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { useTranslation } from 'react-i18next';
import WarningIcon from '@material-ui/icons/Warning';
import { Requirement } from './types';
import { useSharedStyles } from './styles/shared';
import { severityChipClass } from './utils/severity';

interface ForceApprovalWarning {
  ruleId: string;
  severity: 'High' | 'Medium' | 'Low';
  recommendation: string;
  /**
   * Which requirement raised this rule violation. The dialog covers the whole
   * selection, so without it the table is a flat list of rules the user cannot
   * map back to anything — and two requirements breaking the same rule is the
   * normal case, not an edge one.
   */
  requirementId: string;
}

interface ForceApprovalDialogProps {
  open: boolean;
  requirements: Requirement[];
  warnings: ForceApprovalWarning[];
  onConfirm: () => void;
  onCancel: () => void;
}

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.error.main,
  },
  dialogContent: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  alertWarning: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 'bold',
  },
  requirementsBox: {
    marginBottom: theme.spacing(3),
    paddingLeft: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  tableContainer: {
    marginBottom: theme.spacing(2),
    maxHeight: 250,
    overflow: 'auto',
  },
  headerCell: {
    fontWeight: 'bold',
  },
  alertError: {
    marginTop: theme.spacing(2),
  },
  dialogActions: {
    padding: theme.spacing(2),
  },
  confirmButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
}));

const ForceApprovalDialog: React.FC<ForceApprovalDialogProps> = ({
  open,
  requirements,
  warnings,
  onConfirm,
  onCancel,
}) => {
  const shared = useSharedStyles();
  const classes = useStyles();
  const { t } = useTranslation('requirement-doctor');

  return (
    <Dialog open={open} maxWidth="sm" fullWidth onClose={onCancel}>
      <DialogTitle className={classes.dialogTitle}>
        <WarningIcon /> {t('s3.title')}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Alert severity="warning" className={classes.alertWarning}>
          ⚠️ {t('s3.warning', { count: requirements.length })}
        </Alert>

        <Typography variant="subtitle2" className={classes.sectionTitle}>
          {t('s3.affectedRequirements')}
        </Typography>
        <Box className={classes.requirementsBox}>
          {requirements.map(req => (
            <Typography key={req.id} variant="body2">
              • {req.id}:{' '}
              {req.text.length > 50
                ? `${req.text.substring(0, 50)}...`
                : req.text}
            </Typography>
          ))}
        </Box>

        {warnings.length > 0 && (
          <>
            <Typography variant="subtitle2" className={classes.sectionTitle}>
              {t('s3.violatedRules', { count: warnings.length })}
            </Typography>
            <TableContainer
              component={Paper}
              className={classes.tableContainer}
            >
              <Table size="small">
                <TableHead className={shared.tableHead}>
                  <TableRow>
                    <TableCell className={classes.headerCell}>
                      {t('s3.table.columns.requirement')}
                    </TableCell>
                    <TableCell className={classes.headerCell}>
                      {t('s3.table.columns.rule')}
                    </TableCell>
                    <TableCell className={classes.headerCell} align="center">
                      {t('s3.table.columns.severity')}
                    </TableCell>
                    <TableCell className={classes.headerCell}>
                      {t('s3.table.columns.recommendation')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {warnings.map((w, index) => (
                    // The rule id alone is not unique here: the same rule can
                    // be broken by several of the selected requirements.
                    <TableRow key={`${w.requirementId}-${w.ruleId}-${index}`}>
                      <TableCell>{w.requirementId}</TableCell>
                      <TableCell>{w.ruleId}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={w.severity.toUpperCase()}
                          size="small"
                          className={severityChipClass(w.severity, shared)}
                        />
                      </TableCell>
                      <TableCell>{w.recommendation}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        <Alert severity="error" className={classes.alertError}>
          {t('s3.errorMessage')}
        </Alert>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button onClick={onCancel} variant="outlined">
          {t('s3.buttons.cancel')}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          className={classes.confirmButton}
        >
          {t('s3.buttons.forceApprove')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ForceApprovalDialog;
